import React, { Suspense, lazy } from "react";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import Footer from "@/components/Footer";
import ZaloButton from "@/components/ZaloButton";
import ErrorBoundary from "@/components/performance/ErrorBoundary";
// Eager-load FAQ + CallToAction to avoid Suspense whitespace/FOUC mid-page
import FAQSection from "@/components/faq/FAQSection";
import CallToAction from "@/components/CallToAction";
import SEOHelmet from "@/components/seo/SEOHelmet";

// Lazy-loaded components
const Features = lazy(() => import("@/components/Features"));
const HowItWorks = lazy(() => import("@/components/HowItWorks"));
const GuaranteeSection = lazy(() => import("@/components/GuaranteeSection"));
const HiddenCostsSection = lazy(() => import("@/components/HiddenCostsSection"));
const PackagesSection = lazy(() => import("@/components/PackagesSection"));
const ROICalculator = lazy(() => import("@/components/ROICalculator"));
const Testimonials = lazy(() => import("@/components/Testimonials"));
const TrustStats = lazy(() => import("@/components/TrustStats"));

const SectionLoader = () => (
  <div className="py-8 flex justify-center items-center min-h-[120px]">
    <div className="critical-spinner"></div>
  </div>
);

const SectionErrorFallback = ({ sectionName }: { sectionName: string }) => (
  <div className="py-16 flex justify-center items-center bg-muted/30">
    <div className="text-center">
      <p className="text-muted-foreground">Không thể tải phần {sectionName}</p>
      <button 
        onClick={() => window.location.reload()}
        className="mt-2 px-4 py-2 bg-primary text-primary-foreground rounded"
      >
        Thử lại
      </button>
    </div>
  </div>
);

const Index = () => {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    "name": "Propico",
    "image": "https://propico.vn/lovable-uploads/202e7a7e-e459-4302-b6e8-be33ce8b7430.png",
    "url": "https://propico.vn",
    "telephone": "+84-28-7300-8855",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Quận 1",
      "addressLocality": "Thành phố Hồ Chí Minh",
      "addressCountry": "VN"
    },
    "description": "Propico là nền tảng quản lý cho thuê căn hộ chuyên nghiệp trọn gói tại TP.HCM. Phí chỉ từ 8% tiền thuê thực thu.",
    "offers": {
      "@type": "Offer",
      "name": "Dịch vụ Quản lý cho thuê căn hộ (Property Management Service)",
      "priceCurrency": "VND",
      "eligibleRegion": {
        "@type": "Place",
        "name": "Thành phố Hồ Chí Minh"
      }
    }
  };

  return (
    <div className="min-h-screen bg-background pb-16 md:pb-0">
      <SEOHelmet 
        title="Propico - Quản Lý Căn Hộ Cho Thuê Chuyên Nghiệp Tại TP.HCM | Phí Từ 8%"
        description="Propico quản lý cho thuê căn hộ trọn gói tại TP.HCM — tìm khách, sàng lọc, thu tiền hộ, bảo trì 24/7. Phí chỉ từ 8% tiền thuê thực thu, minh bạch hoàn toàn."
        ogTitle="Propico — Quản lý cho thuê căn hộ chuyên nghiệp tại TP.HCM"
        ogDescription="Propico quản lý trọn gói từ A-Z, bảo toàn tài sản và tối ưu thu nhập cho thuê cho chủ nhà. Phí chỉ từ 8%."
        jsonLd={jsonLd}
      />
      <Navbar />
      <div className="pt-20">
        <Hero />

        {/* Trust Stats - right after Hero for immediate credibility */}
        <ErrorBoundary fallback={<SectionErrorFallback sectionName="Trust" />}>
          <Suspense fallback={<SectionLoader />}>
            <TrustStats />
          </Suspense>
        </ErrorBoundary>
        
        <ErrorBoundary fallback={<SectionErrorFallback sectionName="Hidden Costs" />}>
          <Suspense fallback={<SectionLoader />}>
            <HiddenCostsSection />
          </Suspense>
        </ErrorBoundary>
        
        <ErrorBoundary fallback={<SectionErrorFallback sectionName="Guarantee" />}>
          <Suspense fallback={<SectionLoader />}>
            <GuaranteeSection />
          </Suspense>
        </ErrorBoundary>
        
        <ErrorBoundary fallback={<SectionErrorFallback sectionName="Features" />}>
          <Suspense fallback={<SectionLoader />}>
            <Features />
          </Suspense>
        </ErrorBoundary>
        
        <ErrorBoundary fallback={<SectionErrorFallback sectionName="How It Works" />}>
          <Suspense fallback={<SectionLoader />}>
            <HowItWorks />
          </Suspense>
        </ErrorBoundary>
        
        <ErrorBoundary fallback={<SectionErrorFallback sectionName="ROI Calculator" />}>
          <Suspense fallback={<SectionLoader />}>
            <ROICalculator />
          </Suspense>
        </ErrorBoundary>
        
        <ErrorBoundary fallback={<SectionErrorFallback sectionName="Packages" />}>
          <Suspense fallback={<SectionLoader />}>
            <PackagesSection />
          </Suspense>
        </ErrorBoundary>
        
        <ErrorBoundary fallback={<SectionErrorFallback sectionName="Testimonials" />}>
          <Suspense fallback={<SectionLoader />}>
            <Testimonials />
          </Suspense>
        </ErrorBoundary>
        
        <ErrorBoundary fallback={<SectionErrorFallback sectionName="FAQ" />}>
          <FAQSection />
        </ErrorBoundary>

        <div id="property-inquiry-form">
          <ErrorBoundary fallback={<SectionErrorFallback sectionName="Contact Form" />}>
            <CallToAction />
          </ErrorBoundary>
        </div>
        
        <Footer />
      </div>
      
      <ZaloButton />
    </div>
  );
};

export default Index;
